import { normalizeApiError } from "@api/client";

/** @typedef {{ field?: string, issue?: string }} MarketplaceOrderErrorDetail */

export const MARKETPLACE_ORDER_ERROR_CODE = {
  DUPLICATE_PENDING_INTEREST: "DUPLICATE_PENDING_INTEREST",
  FORBIDDEN: "FORBIDDEN",
  NOT_FOUND: "NOT_FOUND",
  INVALID_LISTING_CLASS: "INVALID_LISTING_CLASS",
  INVALID_ORDER_STATUS: "INVALID_ORDER_STATUS",
  MILESTONE_M1_INCOMPLETE: "MILESTONE_M1_INCOMPLETE",
  MILESTONE_INCOMPLETE: "MILESTONE_INCOMPLETE",
  MILESTONE_SEQUENCE_VIOLATION: "MILESTONE_SEQUENCE_VIOLATION",
  INVALID_MILESTONE_PAYLOAD: "INVALID_MILESTONE_PAYLOAD",
};

const CATALOG_MILESTONE_VALIDATION_ISSUES = new Set([
  "catalogValidationActivitiesRequired",
  "catalogValidationActivitiesIncomplete",
  "catalogValidationDoneTickIncomplete",
  "catalogValidationPercentIncomplete",
  "catalogValidationPercentOutOfRange",
  "catalogValidationOutcomeFilesRequired",
  "catalogValidationServiceCompletionRequired",
  "catalogValidationOrderPercentIncomplete",
  "catalogValidationOrderDoneTickIncomplete",
  "catalogValidationOrderActivitiesIncomplete",
]);

const MILESTONE_VALIDATION_ISSUES = new Set([
  "m1ValidationNoFindings",
  "m1ValidationNoComponents",
  "m1ValidationSerialRequired",
  "m1ValidationComponentRequired",
  "m1ValidationEvidenceRequired",
  "m2ValidationPostRepairMeasurementsRequired",
  "m2ValidationHeatTreatmentReferencesRequired",
  "m2ValidationProcessConfirmationRequired",
  "m3ValidationFinalInspectionReportRequired",
  "m3ValidationServiceCompletionRequired",
  "m4ValidationTitleRequired",
  "followUpValidationTitleRequired",
  "followUpValidationSectionRequired",
  "followUpValidationSectionEvidenceRequired",
]);

/**
 * @param {unknown} error
 * @returns {{ code?: string, message?: string, details?: MarketplaceOrderErrorDetail[], status?: number }|null}
 */
export function getNormalizedMarketplaceOrderError(error) {
  if (error && typeof error === "object" && "normalizedApiError" in error) {
    return /** @type {{ normalizedApiError?: object }} */ (error).normalizedApiError ?? null;
  }
  if (error && typeof error === "object" && "response" in error) {
    return normalizeApiError(/** @type {import("axios").AxiosError} */ (error));
  }
  return null;
}

/**
 * Milestone validation `issue` from `error.details[]` (`field: "reason"`).
 * @param {unknown} error
 * @returns {string|null}
 */
export function getMilestoneValidationIssueFromError(error) {
  const normalized = getNormalizedMarketplaceOrderError(error);
  const details = normalized?.details;
  if (!Array.isArray(details)) return null;

  for (const entry of details) {
    if (!entry || typeof entry !== "object") continue;
    const field = /** @type {{ field?: string, issue?: string }} */ (entry).field;
    const issue = /** @type {{ field?: string, issue?: string }} */ (entry).issue;
    if (field !== "reason" || typeof issue !== "string") continue;
    if (
      MILESTONE_VALIDATION_ISSUES.has(issue) ||
      CATALOG_MILESTONE_VALIDATION_ISSUES.has(issue)
    ) {
      return issue;
    }
  }

  return null;
}

function isStaleCatalogMilestoneApiError(error) {
  const normalized = getNormalizedMarketplaceOrderError(error);
  if (normalized?.code === "CATALOG_MILESTONE_INCOMPLETE") {
    return true;
  }
  return /activities must be complete/i.test(String(normalized?.message ?? ""));
}

/**
 * Translation key under `orders` for a milestone validation issue code.
 * @param {string|null} issue
 * @returns {string|null}
 */
export function m1ValidationTranslationKey(issue) {
  if (!issue || !MILESTONE_VALIDATION_ISSUES.has(issue)) return null;
  return issue;
}

/**
 * Toast copy for marketplace order actions (accept/decline/cancel/advance).
 * Backend maps some failures to `error.code: "REQUEST_FAILED"` — prefer `error.message`.
 *
 * @param {unknown} error
 * @param {string} [fallback]
 * @returns {string}
 */
export function getMarketplaceOrderActionErrorMessage(
  error,
  fallback = "Request failed"
) {
  const normalized = getNormalizedMarketplaceOrderError(error);
  const message =
    typeof normalized?.message === "string" ? normalized.message.trim() : "";

  return message || fallback;
}

/**
 * User-facing message for milestone PUT failures (M1 validation, sequence, etc.).
 *
 * @param {unknown} error
 * @param {(key: string) => string} t
 * @param {string} fallback
 * @returns {string}
 */
export function getRemanufacturingMilestoneErrorMessage(error, t, fallback) {
  const validationIssue = getMilestoneValidationIssueFromError(error);
  const validationKey = m1ValidationTranslationKey(validationIssue);
  if (validationKey) {
    return t(validationKey);
  }
  if (
    validationIssue &&
    CATALOG_MILESTONE_VALIDATION_ISSUES.has(validationIssue)
  ) {
    const translated = t(validationIssue);
    if (translated !== validationIssue) {
      return translated;
    }
  }

  if (isStaleCatalogMilestoneApiError(error)) {
    return t("catalogApiStaleMilestonePercentRule");
  }

  const normalized = getNormalizedMarketplaceOrderError(error);
  if (normalized?.code === MARKETPLACE_ORDER_ERROR_CODE.MILESTONE_SEQUENCE_VIOLATION) {
    return (
      getMarketplaceOrderActionErrorMessage(error, "") ||
      "Complete milestones in order (M1, then M2, then M3)."
    );
  }

  return getMarketplaceOrderActionErrorMessage(error, fallback);
}
